import {
  ExtendedRefs,
  FloatingContext,
  Placement,
  ReferenceType,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from "@floating-ui/react";
import {
  CSSProperties,
  HTMLProps,
  MutableRefObject,
  ReactNode,
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface DropdownContextState {
  isOpen: boolean;
  close: () => void;
  open: () => void;
  autoClose?: boolean;

  listRef: MutableRefObject<(HTMLElement | null)[]>;
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;

  onSelect?: (
    eventKey: string,
    e: React.SyntheticEvent<unknown> | undefined
  ) => void;

  floatingRefs?: ExtendedRefs<ReferenceType>;
  floatingStyles?: CSSProperties;
  floatingContext?: FloatingContext<ReferenceType>;
  getReferenceProps: (
    userProps?: HTMLProps<Element> | undefined
  ) => Record<string, unknown>;
  getFloatingProps: (
    userProps?: HTMLProps<HTMLElement> | undefined
  ) => Record<string, unknown>;
  getItemProps: (
    userProps?: React.HTMLProps<HTMLElement>
  ) => Record<string, unknown>;
}

export const DropdownContext = createContext<DropdownContextState>({
  isOpen: false,
  close: () => {},
  open: () => {},
  listRef: { current: [] },
  activeIndex: null,
  setActiveIndex: () => {},
  getReferenceProps: () => ({}),
  getFloatingProps: () => ({}),
  getItemProps: () => ({}),
});

export interface DropdownProps {
  children: ReactNode;
  autoClose?: boolean;
  menuPlacement?: Placement;
  menuMaxHeight?: number;
  onSelect?: (eventKey: string, e?: React.SyntheticEvent<unknown>) => void;
  onClose?: () => void;
  onOpen?: () => void;
}

const Dropdown = (props: Readonly<DropdownProps>) => {
  const {
    onSelect,
    onOpen,
    onClose,
    autoClose = true,
    menuPlacement = "bottom-start",
    menuMaxHeight = 250,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const listRef = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy: "fixed",
    middleware: [
      offset(8),
      flip(),
      shift(),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${
              availableHeight < menuMaxHeight ? availableHeight : menuMaxHeight
            }px`,
            minWidth: "fit-content",
          });
        },
        padding: 10,
      }),
    ],
    whileElementsMounted: autoUpdate,
    placement: menuPlacement,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, role, listNav]
  );

  const isOpenRef = useRef(isOpen);

  useEffect(() => {
    if (isOpen) onOpen?.();
    else if (isOpen != isOpenRef.current) onClose?.();
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const dropdownContext = useMemo<DropdownContextState>(
    () => ({
      isOpen: isOpen,
      close: () => setIsOpen(false),
      open: () => setIsOpen(true),
      autoClose: autoClose,
      listRef: listRef,
      activeIndex: activeIndex,
      setActiveIndex: setActiveIndex,
      onSelect: onSelect,
      floatingRefs: refs,
      floatingStyles: floatingStyles,
      floatingContext: context,
      getReferenceProps: getReferenceProps,
      getFloatingProps: getFloatingProps,
      getItemProps: getItemProps,
    }),
    [
      isOpen,
      autoClose,
      listRef,
      activeIndex,
      setActiveIndex,
      onSelect,
      refs,
      floatingStyles,
      context,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
    ]
  );

  return (
    <DropdownContext.Provider value={dropdownContext}>
      {props.children}
    </DropdownContext.Provider>
  );
};

export default Dropdown;
